<script setup lang="ts">
import Highcharts from "highcharts";
import { computed, ref } from "vue";

import { EDashboardType } from "@/app/data/dashboard/dashboard.types";
import { TCategorizedDataResponseSerie } from "@/components/ui/chart/types/categorized-chart-series.type";
import { EDataUnit } from "@/constants/data-units.type";
import { TCommonConsumption } from "@/data/api/kili/consumption/types/common-consumption.type";
import { EConsumptionDistributionApplianceCategory } from "@/data/api/kili/consumption/types/consumption-distribution.type";
import { EConsumptionDistribution } from "@/data/api/kili/consumption/types/consumption-metrics.type";
import { keyOf, useTypedIntl } from "@/locales/typed-intl";
import { useDashboardStore } from "@/stores/dashboard/dashboard-store";
import { PieSeriesAdapter } from "@/utils/chart/adapters/series/pie-chart-series-adapter";
import {
  buildPieChartOptions,
  grayStrokePattern,
  IPieChartSpecs,
  pieChartDefaultColors,
} from "@/utils/chart/pie-chart-options-utils";
import { isDefined, makeGuard } from "@/utils/object";
import { getMaybeValuePrefixSymbol } from "@/utils/translation-utils";

import DashboardCard from "@/components/data/dashboard/dashboard-card.vue";
import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";
import BaseChart from "@/components/ui/chart/base-chart.vue";

interface IProps {
  consumption?: TCommonConsumption;
  isLoading?: boolean;
  type: EDashboardType;
  hasNoData?: boolean;
}

const props = defineProps<IProps>();

const { t, k } = useTypedIntl();
const dashboardStore = useDashboardStore();

const isInfosDialogVisible = ref<boolean>(false);

const emptySerie: TCategorizedDataResponseSerie<EConsumptionDistribution> = {
  kind: EConsumptionDistribution.DEVICES_CONSUMPTION_DISTRIBUTION,
  unit: EDataUnit.WATT_HOUR,
  value: [
    {
      name: EConsumptionDistributionApplianceCategory.HEATER,
      value: null,
    },
    {
      name: EConsumptionDistributionApplianceCategory.WATER_HEATER,
      value: null,
    },
    {
      name: EConsumptionDistributionApplianceCategory.OTHER,
      value: null,
    },
  ],
};

const initialSerie = computed<
  TCategorizedDataResponseSerie<EConsumptionDistribution.DEVICES_CONSUMPTION_DISTRIBUTION> | undefined
>(() => {
  return props.consumption?.devicesByCategory
    ? {
        kind: EConsumptionDistribution.DEVICES_CONSUMPTION_DISTRIBUTION,
        unit: props.consumption.devicesByCategory.unit,
        value: props.consumption.devicesByCategory.value.map((item) => {
          return {
            name: item.category,
            value: item.consumption,
          };
        }),
      }
    : undefined;
});

const calculatedSerie = computed<TCategorizedDataResponseSerie<EConsumptionDistribution> | undefined>(() => {
  const totalDevices = props.consumption?.totalDevices.value;
  const globalConsumption = props.consumption?.global.value;

  if (!initialSerie.value || typeof totalDevices !== "number") return;

  // if total dso consumption is not defined or less than devices total consumption, no alterations are needed
  if (typeof globalConsumption !== "number" || globalConsumption < totalDevices) {
    return initialSerie.value;
  }

  // otherwise, 'other appliance categories' consumption should be the addition of the initial 'other appliance categories' +
  // the difference between total dso consumption and total devices consumption.
  // The sum of all devices consumption should be equal to the total dso consumption
  const serieValueWithoutOther = initialSerie.value.value.filter(
    (item) => item.name !== EConsumptionDistributionApplianceCategory.OTHER,
  );
  const maybeOtherConsumption = initialSerie.value.value.find(
    (item) => item.name === EConsumptionDistributionApplianceCategory.OTHER,
  );

  return {
    ...initialSerie.value,
    value: [
      ...serieValueWithoutOther,
      {
        name: EConsumptionDistributionApplianceCategory.OTHER,
        value: globalConsumption - totalDevices + (maybeOtherConsumption?.value ?? 0),
      },
    ],
  };
});

const displayedSerie = computed<TCategorizedDataResponseSerie<EConsumptionDistribution>>(() => {
  const serie: TCategorizedDataResponseSerie<EConsumptionDistribution> = calculatedSerie.value?.value.length
    ? calculatedSerie.value
    : emptySerie;

  const translatedValues = serie.value.map((val) => {
    return {
      ...val,
      name: getTranslatedCategory(val.name),
    };
  });

  return {
    ...serie,
    value: translatedValues,
  };
});

const consumptionDistributionChartSpecs = computed<Partial<IPieChartSpecs<EConsumptionDistribution>>>(() => {
  const serie: TCategorizedDataResponseSerie<EConsumptionDistribution> = calculatedSerie.value?.value.length
    ? calculatedSerie.value
    : emptySerie;

  const allWhValues: number[] = serie.value.map((pt) => pt.value).filter((pt) => isDefined(pt));

  const highestValue = Math.max(...allWhValues);
  const maybeSymbol = getMaybeValuePrefixSymbol(highestValue);

  const serieLength: number = serie.value.length;

  const hasOtherAppliancesCategory: boolean = !!serie.value.find((serie) => {
    return serie.name === EConsumptionDistributionApplianceCategory.OTHER;
  });

  const colors: (string | Highcharts.PatternObject)[] = hasOtherAppliancesCategory
    ? [...pieChartDefaultColors.slice(0, serieLength - 1), grayStrokePattern]
    : pieChartDefaultColors;

  return {
    seriesAdapter: [
      [
        EConsumptionDistribution.DEVICES_CONSUMPTION_DISTRIBUTION,
        PieSeriesAdapter({
          name: t(k.global.consumption),
          prefixSymbol: maybeSymbol,
          accessibilityDescription: t(k.data.dashboard.deviceConsumptionDistribution.description[props.type]),
        }),
      ],
    ],
    isDonut: true,
    showTotal: true,
    colors,
    prefixSymbol: maybeSymbol,
    accessibilityDescription: t(
      k.data.dashboard.deviceConsumptionDistribution.tooltip({
        equipmentConsumption: t(k.data.dashboard.insights.equipmentConsumption.title),
      }),
    ),
    legendWidth: 130,
  };
});

const chartOpts = computed(() => {
  return buildPieChartOptions<EConsumptionDistribution>(consumptionDistributionChartSpecs.value, displayedSerie.value);
});

const isConsumptionDistributionApplianceCategory = makeGuard(Object.values(EConsumptionDistributionApplianceCategory));

function getTranslatedCategory(category: string): string {
  if (!isConsumptionDistributionApplianceCategory(category)) {
    return category;
  }

  return t(k.data.dashboard.deviceConsumptionDistribution.deviceCategory[category]);
}

function handleShowDevicesTotalConsumptionInfos() {
  isInfosDialogVisible.value = false;
  dashboardStore.showDevicesTotalConsumptionInfos();
}
</script>

<template>
  <DashboardCard
    v-model:infos-visible="isInfosDialogVisible"
    :infos="true"
    :subtitle="
      t(
        k.data.dashboard.deviceConsumptionDistribution.subtitle({
          amount: props.consumption?.devicesCount ?? 0,
        }),
      )
    "
    :title="t(k.data.dashboard.deviceConsumptionDistribution.title)"
  >
    <template #title-icon>
      <div class="bg-gray-100 rounded-full !w-[20px] !h-[20px] flex justify-center items-center">
        <i class="pi pi-bolt !text-xs text-text-secondary" />
      </div>
    </template>

    <template #infos>
      <i18n-t
        class="whitespace-pre-line"
        :keypath="keyOf(k.data.dashboard.deviceConsumptionDistribution.tooltip)"
        tag="p"
      >
        <template #equipmentConsumption>
          <HospitalityButton
            severity="link"
            size="xs"
            @click="handleShowDevicesTotalConsumptionInfos()"
            >{{ t(k.data.dashboard.insights.equipmentConsumption.title) }}</HospitalityButton
          >
        </template>
      </i18n-t>
    </template>

    <BaseChart
      :has-no-data="props.hasNoData"
      :is-loading="props.isLoading"
      :options="chartOpts"
    >
      <template #noDataMessage>
        <slot name="noDataMessage" />
      </template>
    </BaseChart>
  </DashboardCard>
</template>
